
import { useState } from "react";
import PractitionerCard from "./PractitionerCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Practitioner } from "@/types/practitioner";
import { AlertCircle } from "lucide-react";
import { useTranslation } from 'react-i18next';

interface PractitionerListProps {
  practitioners: Practitioner[];
  sortBy: string;
  onSortChange: (sortOption: string) => void;
  isLoading: boolean;
  error: string | null;
  setShowSidebar: (show: boolean) => void;
  hasMore: boolean;
  onLoadMore: () => void;
}

const PractitionerList = ({ 
  practitioners, 
  sortBy, 
  onSortChange, 
  isLoading, 
  error,
  setShowSidebar,
  hasMore,
  onLoadMore
}: PractitionerListProps) => {
  const { t } = useTranslation('practitioners');

  // Show loading skeletons
  if (isLoading) {
    return (
      <div className="w-full">
        <div className="mb-4 flex justify-end">
          <div className="w-32 h-8 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="flex flex-col md:flex-row h-full">
                <div className="md:w-1/3 h-48 md:h-auto">
                  <Skeleton className="w-full h-full" />
                </div>
                <div className="md:w-2/3 p-6">
                  <Skeleton className="h-6 w-1/2 mb-2" />
                  <Skeleton className="h-4 w-1/3 mb-4" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Show error message
  if (error) {
    return (
      <div className="w-full">
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <h3 className="text-lg font-medium text-red-700 mb-1">{t('errorLoadingPractitioners')}</h3>
          <p className="text-red-600">{error}</p>
          <p className="mt-2 text-sm text-red-600">{t('refreshOrContact')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-4 flex justify-between items-center">
        <div className="text-sm text-earth-600">
          {practitioners.length} {practitioners.length === 1 ? t('practitionerFound') : t('practitionersFound')}
        </div>
        <div className="flex items-center">
          <span className="text-sm text-earth-600 mr-2">{t('sortBy')}:</span>
          <select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
            className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
          >
            <option value="rating">{t('highestRated')}</option>
            <option value="orders">{t('mostExperienced')}</option>
            <option value="distance">{t('nearest')}</option>
          </select>
        </div>
      </div>
      
      <div className="space-y-6">
        {practitioners.length > 0 ? (
          practitioners.map(practitioner => (
                <PractitionerCard
                  key={practitioner.id}
                  practitioner={practitioner}
                  isActive={false}
                />
          ))
        ) : (
          <div className="text-center py-12 bg-amber-50 rounded-lg">
            <p className="text-lg text-earth-700">{t('noPractitionersMatch')}</p>
            <p className="text-earth-500">{t('tryAdjustingFilters')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PractitionerList;
